import DeleteConfirmationDialog from '@components/DeleteConfirmation';
import EstimationDialog from '@components/EstimationDialog';
import { CircularProgressWithLabel } from '@components/common/CircularProgressWithLabel';
import { useEntitlementError } from '@context/EntitlementContext';
import { useMixpanel } from '@context/MixpanelProvider';
import { useEstimateCredits } from '@hooks/postQueries';
import { useCheckCredits } from '@hooks/useCheckCredits';
import { useWorkspaceUsage } from '@hooks/getQueries';
import useConfirmationDialog from '@hooks/useDeleteConfirmation';
import useWorkspace from '@hooks/useWorkspace';
import { Box, Link, Tab, Tabs, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles'; // Add this import
import { INTERVIEW_UPLOAD_EVENTS } from '@utils/eventSchema';
import { showErrorToast, showSuccessToast } from '@utils/toast';
import axios from 'axios';
import { Upload } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom'; // Add this import at the top
import {
  useMultiCommitBlocks,
  useMultiGenerateUploadUrl,
  useSaveAndAnalyzeInterviewAudioVideo,
} from '../../../hooks/postQueries';
import { categorizeFileType } from './CategorizeFileType';
import { ConfigureDialog } from './ConfigureDialog';
import UploadHeader from './UploadHeader';
import UploadedFiles from './UploadedFiles';

const FILE_SIZE_LIMITS = {
  text: 50 * 1024 * 1024, // 50 MB for text
  audio: 500 * 1024 * 1024, // 500 MB for audio
  video: 2 * 1024 * 1024 * 1024, // 2 GB for video
};
const defaultOnFilesReady = () => {};

const FileUpload = ({
  onClose,
  width = '556px',
  interviewId,
  onFilesReady = defaultOnFilesReady,
  showHeader = true,
}) => {
  const theme = useTheme();
  const params = useParams();

  const id = params.id || interviewId;

  const [showEstimationDialog, setShowEstimationDialog] = useState(false);
  const { data: estimateCredits } = useEstimateCredits();
  const processId = estimateCredits?.process_id;
  const [currentTab, setCurrentTab] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [pendingUploads, setPendingUploads] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [totalProgress, setTotalProgress] = useState(0);
  const [configurationData, setConfigurationData] = useState({
    language: '',
    selectedOptions: [],
  });
  const { trackEvent } = useMixpanel();
  const { open, show, handleConfirm, handleClose } = useConfirmationDialog();
  const [creditsNeeded] = useState(null);
  const { NoCreditDialog } = useCheckCredits(creditsNeeded);
  const { activeWorkspaceId } = useWorkspace();
  const { data: workspaceUsage } = useWorkspaceUsage(activeWorkspaceId);
  const { showInsufficientEntitlementsError } = useEntitlementError();
  const remainingUploadsRaw = workspaceUsage?.entitlements?.transcription_uploads_remaining;
  const remainingUploads = typeof remainingUploadsRaw === 'number' ? remainingUploadsRaw : null;
  const maxFiles = remainingUploads !== null ? remainingUploads : Infinity;
  const [uploadedFilesProgressCount, setUploadedFilesProgressCount] = useState(0);

  const generateMultipleUploadUrl = useMultiGenerateUploadUrl();
  const multiCommitBlocks = useMultiCommitBlocks();
  const saveAndAnalyzeInterviewAudioVideo = useSaveAndAnalyzeInterviewAudioVideo();
  const BLOCK_SIZE = 4 * 1024 * 1024; // 4MB

  useEffect(() => {
    // Track initial upload screen view
    trackEvent('interviewupload', {
      ...INTERVIEW_UPLOAD_EVENTS.INTERVIEW_VIEWED_UPLOAD_SCREEN,
    });
  }, []);

  const splitFileIntoBlocks = (file) => {
    const blocks = [];
    let start = 0;
    while (start < file.size) {
      const end = Math.min(start + BLOCK_SIZE, file.size);
      blocks.push(file.slice(start, end));
      start = end;
    }
    return blocks;
  };

  const generateBlockId = (index) => {
    return btoa(`${index.toString().padStart(32, '0')}`);
  };

  const uploadBlock = async (block, blockId, uploadUrl) => {
    const blockUrl = `${uploadUrl}&comp=block&blockid=${blockId}`;

    await axios.put(blockUrl, block, {
      headers: {
        'Content-Type': block.type,
        'x-ms-blob-type': 'BlockBlob',
      },
      onUploadProgress: () => {},
    });
  };

  const handleBatchUpload = async (files) => {
    try {
      setIsUploading(true);
      setUploadingFiles(files);

      const uploadRequests = files.map((file) => ({
        filename: file.name,
        content_type: file.type,
        file_size: file.size,
      }));

      const payload = {
        uploads: uploadRequests,
        study_id: id,
      };
      // console.log('the payload in handleBatchUpload is:', payload);
      const uploadUrlResponse = await generateMultipleUploadUrl.mutateAsync(payload);

      const uploadResults = [];
      const commitRequests = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const uploadInfo = uploadUrlResponse.uploads[i];
        // console.log('Upload info:', uploadInfo);

        const blocks = splitFileIntoBlocks(file);
        const blockIds = [];

        for (let j = 0; j < blocks.length; j++) {
          const blockId = generateBlockId(j);
          blockIds.push(blockId);
          await uploadBlock(
            blocks[j],
            blockId,
            uploadInfo?.upload_data?.upload_url,
            j,
            blocks.length,
          );
          setTotalProgress((i * 100 + ((j + 1) * 100) / blocks.length) / files.length);
        }
        setUploadedFilesProgressCount((prev) => prev + 1);
        commitRequests.push({
          blob_name: file.name,
          block_ids: blockIds,
          content_type: file.type,
          upload_url: uploadInfo?.upload_data?.upload_url,
        });

        // Store file info temporarily without status
        uploadResults.push({
          name: file.name,
          path: file.name, // keep both name and path for compatibility
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
          blob_url: uploadInfo?.upload_data?.upload_url,
        });
      }

      const commitResult = await multiCommitBlocks.mutateAsync({
        uploads: commitRequests,
      });
      setUploadedFilesProgressCount(0);
      // Update uploadResults with status from commitResult
      const finalUploadResults = uploadResults.map((file) => {
        const commitData = commitResult.results.find((result) => result.blob_name === file.name);
        return {
          ...file,
          status: commitData?.status || 'failed', // Add status from commit result
          url: commitData?.url || '', // Optionally store the final URL
        };
      });

      setPendingUploads(finalUploadResults);
      return true;
    } catch (error) {
      // console.error('Error details while uploading files:', error);
      showErrorToast(`Failed to upload files: ${error?.detail[0]?.msg}`);
      return false;
    } finally {
      setIsUploading(false);
      setUploadingFiles([]);
      setTotalProgress(0);
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (isUploading) {
        return;
      }

      trackEvent('interviewupload', {
        ...INTERVIEW_UPLOAD_EVENTS.INTERVIEW_FILE_UPLOAD_STARTED,
        file_count: acceptedFiles.length,
        file_types: acceptedFiles.map((file) => file.type).join(', '),
        file_sizes: acceptedFiles.map((file) => file.size).join(', '),
      });

      if (remainingUploads !== null) {
        const pendingCount = pendingUploads.length + uploadedFiles.length;
        const availableUploads = Math.max(remainingUploads - pendingCount, 0);
        if (acceptedFiles.length > availableUploads) {
          showInsufficientEntitlementsError({
            entitlementType: 'transcription_uploads',
            required: acceptedFiles.length,
            available: availableUploads,
          });
          return;
        }
      }

      if (!acceptedFiles || acceptedFiles.length === 0) {
        toast.error('No files received');
      }

      // Filter out files larger than their respective limits
      const validFiles = acceptedFiles.filter((file) => {
        const sizeLimit =
          currentTab === 0
            ? FILE_SIZE_LIMITS.text
            : currentTab === 1
              ? FILE_SIZE_LIMITS.audio
              : FILE_SIZE_LIMITS.video;

        if (file.size > sizeLimit) {
          const limitInMB = sizeLimit / (1024 * 1024);
          showErrorToast(`File ${file.name} exceeds ${limitInMB}MB limit`);
          return false;
        }
        return true;
      });

      if (validFiles.length > 0) {
        handleBatchUpload(validFiles);
      }
    },
    [
      isUploading,
      uploadedFiles,
      currentTab,
      pendingUploads,
      remainingUploads,
      showInsufficientEntitlementsError,
    ],
  );

  // Modify dropzone config to accept multiple files
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize:
      currentTab === 0
        ? FILE_SIZE_LIMITS.text
        : currentTab === 1
          ? FILE_SIZE_LIMITS.audio
          : FILE_SIZE_LIMITS.video,
    accept: {
      'text/*': ['.txt', '.docx', '.pdf'],
      'audio/*': ['.mp3', '.wav', '.ogg', '.m4a', '.flac', '.aac', 'mpeg'],
      'video/*': ['.mp4', '.webm', '.avi', '.mov', '.mkv'],
    }[currentTab === 0 ? 'text/*' : currentTab === 1 ? 'audio/*' : 'video/*'],
    disabled: isUploading,
    multiple: true, // Enable multiple file selection
    ...(Number.isFinite(maxFiles) ? { maxFiles } : {}),
    onError: (err) => {
      toast.error('Dropzone error: ' + err.message);
    },
  });

  const handleRemoveFile = (fileId) => {
    setUploadedFiles((files) => files.filter((f) => f.id !== fileId));
  };

  const handleConfigureFiles = (config) => {
    setConfigurationData(config);
    const featureFlags = {
      summarization: false,
      auto_chapters: false,
      important_phrases: false,
      entity_detection: false,
      topic_detection: false,
      profanity_filtering: false,
    };

    // Enable the selected options
    config.selectedOptions.forEach((option) => {
      const key = option.toLowerCase().replace(/\s+/g, '_');
      if (key in featureFlags) {
        featureFlags[key] = true;
      }
    });
    const newFiles = pendingUploads.map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      path: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      blob_url: file.blob_url,
      status: file.status,
      url: file.url,
      language_code: config.language,
      ...featureFlags,
    }));

    setUploadedFiles((prev) => [...prev, ...newFiles]);
    setPendingUploads([]);

    trackEvent('interviewupload', {
      ...INTERVIEW_UPLOAD_EVENTS.INTERVIEW_LANGUAGE_SELECTED,
      language: config.language,
    });
    trackEvent('interviewupload', {
      ...INTERVIEW_UPLOAD_EVENTS.INTERVIEW_FILE_ADDED,
      file_count: newFiles.length,
      file_type: newFiles[0].type.split('/')[0], // Assuming all files are of the same type
      language: config.language,
    });
  };

  const handleRemoveConfigFile = (fileToRemove) => {
    setPendingUploads(pendingUploads.filter((file) => file.name !== fileToRemove.name));
  };
  const handleEstimationAgree = async () => {
    if (uploadedFiles.length === 0) return;

    const payload = {
      blob_info: uploadedFiles.map((file) => ({
        blob_name: file?.name,
        url: file?.url,
        status: file?.status,
        type: categorizeFileType(file?.type),
        file_content_type: file?.type,
        country: configurationData.language,
        is_transcribed: false,
      })),
      language_code: configurationData.language,
    };

    try {
      const response = await saveAndAnalyzeInterviewAudioVideo.mutateAsync({
        id,
        processId,
        ...payload,
      });
      if (response) {
        trackEvent('interviewupload', {
          ...INTERVIEW_UPLOAD_EVENTS.INTERVIEW_FILE_UPLOAD_COMPLETED,
          file_count: uploadedFiles.length,
          file_types: uploadedFiles.map((file) => file.type).join(', '),
        });
      }
      showSuccessToast(
        "Your interview data has been uploaded. We'll notify you as soon as the transcript is ready",
        '500px',
      );
      onClose();
    } catch (error) {
      console.log('the error is', error);
      showErrorToast('Failed to process files');
    }
  };

  // Use the mutation's isPending state for reliable loading indication
  const isSubmitting = saveAndAnalyzeInterviewAudioVideo.isPending;

  const handleDone = async () => {
    // Prevent double-click by checking if already submitting
    if (uploadedFiles.length === 0 || isSubmitting) return;

    const payload = {
      blob_info: uploadedFiles.map((file) => ({
        blob_name: file?.name,
        url: file?.url,
        status: file?.status,
        type: categorizeFileType(file?.type),
        file_content_type: file?.type,
        country: configurationData.language,
        is_transcribed: false,
      })),
      language_code: configurationData.language,
    };

    // Pass the payload to parent component directly
    onFilesReady(payload);

    try {
      await saveAndAnalyzeInterviewAudioVideo.mutateAsync({
        id,
        processId: processId ?? '',
        ...payload,
      });

      trackEvent('interviewupload', {
        ...INTERVIEW_UPLOAD_EVENTS.INTERVIEW_FILE_UPLOAD_COMPLETED,
        file_count: uploadedFiles.length,
        file_types: uploadedFiles.map((file) => file.type).join(', '),
      });
      showSuccessToast(
        "Your interview data has been uploaded. We'll notify you as soon as the transcript is ready",
      );
      onClose();
    } catch (error) {
      showErrorToast('Failed to process files');
    }
  };

  const handleCancelUpload = () => {
    show(() => {
      onClose();
    });
  };

  useEffect(() => {
    // Only call onFilesReady if it's not the default function
    if (onFilesReady !== defaultOnFilesReady) {
      if (uploadedFiles.length > 0) {
        const payload = {
          blob_info: uploadedFiles.map((file) => ({
            blob_name: file?.name,
            url: file?.url,
            status: file?.status,
            type: categorizeFileType(file?.type),
            file_content_type: file?.type,
            country: configurationData.language,
            is_transcribed: false,
          })),
          language_code: configurationData.language,
        };
        onFilesReady(payload);
      } else {
        onFilesReady(null);
      }
    }
  }, [uploadedFiles, configurationData]);

  const getFileSizeLimitText = () => {
    switch (currentTab) {
      case 0:
        return '50MB';
      case 1:
        return '500MB';
      case 2:
        return '2GB';
      default:
        return '50MB';
    }
  };

  const uploadingContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%', // Use full height of parent
        width: '100%', // Use full width of parent
        gap: 1,
        padding: 2,
      }}
    >
      <CircularProgressWithLabel variant="determinate" value={totalProgress} />
      <Typography
        variant="body1"
        sx={{
          textAlign: 'center',
        }}
      >
        Uploading {`${uploadedFilesProgressCount}/${uploadingFiles.length}`}
        {uploadingFiles.length === 1 ? ' file' : ' files'}...
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          textAlign: 'center',
          fontSize: {
            xs: '0.75rem',
            sm: '0.875rem',
          },
        }}
      >
        This process may take a while, depending on the file sizes.
      </Typography>
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'white',
          width: { xs: '90%', sm: '90%', md: width },
          height: showHeader ? 452 : 412,
          maxWidth: width,
          padding: '0px',
          border: '2px solid #E8E8E8',
        }}
      >
        <Box
          sx={{
            overflow: 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header component */}
          {showHeader && (
            <UploadHeader
              onCancel={handleCancelUpload}
              onDone={handleDone}
              hasFiles={uploadedFiles.length > 0}
              isSubmitting={isSubmitting}
            />
          )}

          {/* Description and tabs sections remain the same */}
          <Typography
            id="parent-modal-description"
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: {
                xs: '12px',
                sm: '14px',
              },
              fontWeight: 400,
              px: 2,
              py: 1,
              pt: showHeader ? 1 : 3,
            }}
          >
            Please upload one file per interview/focus group session. You can upload a maximum of
            {remainingUploads !== null ? ` ${remainingUploads} ` : ' multiple '}
            files of different formats based on your remaining transcription uploads. If you need
            additional capacity, please{' '}
            <Link
              href="https://qualz.ai/contactus"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ cursor: 'pointer', textDecoration: 'none' }}
            >
              contact us
            </Link>
            .
          </Typography>

          {/* Tabs section */}
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              height: '48px',
              // backgroundColor: 'red',
              ml: 2,
              mr: 2,
              mb: {
                xs: 1,
                sm: 2,
                md: 3,
              },
            }}
          >
            <Tabs
              value={currentTab}
              onChange={(_, value) => setCurrentTab(value)}
              sx={{
                '& .MuiTab-root': {
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  minWidth: 'auto', // Prevents full-width tabs
                },
                '& .MuiTabs-indicator': {
                  height: '0.188rem', // Thicker border
                  width: '40%', // Adjusts the indicator width
                  left: '30%', // Centers it under the tab
                },
              }}
            >
              {/* Implement text later */}
              <Tab label="Text" sx={{ padding: '0px', mr: 3 }} />
              <Tab label="Audio" sx={{ padding: '0px', mr: 3 }} />
              <Tab label="Video" sx={{ padding: '0px' }} />
            </Tabs>
          </Box>

          {/* Dropzone section */}
          <Box
            {...getRootProps()}
            onClick={(e) => {
              // console.log('Dropzone clicked');
              getRootProps().onClick(e);
            }}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              borderRadius: 1,
              height: { xs: 224, sm: 224 },
              textAlign: 'center',
              bgcolor: 'grey.50',
              cursor: isUploading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 2,
            }}
          >
            <input {...getInputProps()} />
            {isUploading ? (
              uploadingContent
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Upload size={16} color={theme.palette.primary.main} sx={{ mb: 1 }} />
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: '600',
                    fontSize: {
                      xs: '0.9rem',
                      sm: '1rem',
                    },
                    px: {
                      xs: 1,
                      sm: 2,
                    },
                  }}
                >
                  Drag and drop or click to upload{' '}
                  {currentTab === 0 ? 'text' : currentTab === 1 ? 'audio' : 'video'} files
                </Typography>
                <Typography
                  variant="body2"
                  color="#6B6B6B"
                  sx={{
                    fontSize: {
                      xs: '0.75rem',
                      sm: '0.875rem',
                    },
                  }}
                >
                  Supports all{' '}
                  {currentTab === 0
                    ? 'text, docx & pdf '
                    : currentTab === 1
                      ? 'mp3, mpeg, wav, ogg, m4a, flac & aac '
                      : 'mp4, webm, avi, mov & mkv '}{' '}
                  formats.
                </Typography>
                <Typography
                  variant="body2"
                  color="#6B6B6B"
                  sx={{
                    fontSize: {
                      xs: '0.75rem',
                      sm: '0.875rem',
                    },
                  }}
                >
                  Up to {getFileSizeLimitText()} per file.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Configure Dialog */}
        <ConfigureDialog
          currentTab={currentTab}
          open={pendingUploads.length > 0}
          files={pendingUploads}
          onClose={() => setPendingUploads([])}
          onAdd={handleConfigureFiles}
          onRemoveFile={handleRemoveConfigFile}
        />
      </Box>

      {/* Uploaded files component */}
      <UploadedFiles
        files={uploadedFiles}
        onRemoveFile={handleRemoveFile}
        width={width}
        maxFiles={maxFiles}
      />

      {/* Dialogs */}
      <NoCreditDialog />
      <EstimationDialog
        open={showEstimationDialog}
        onClose={() => setShowEstimationDialog(false)}
        onAgree={handleEstimationAgree}
        title="This action requires additional entitlements"
        description="This will incur additional entitlements."
        estimateCredits={estimateCredits}
      />
      <DeleteConfirmationDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleConfirm}
        okText="Yes"
        noText="No"
        title="Cancel Upload"
        content="Your progress will be lost. Do you wish to cancel?"
      />
    </>
  );
};

export default FileUpload;
